/**
 * Address Utilities
 *
 * Helper functions for working with addresses.
 * Ported from breitling-storefront-next lib/address.ts
 */

import type { SaleorAddressInput } from '@/graphql/saleor/api';
import {
  ACCOUNT_METADATA,
  getJapaneseNameFromMetadata,
  getMetaValue,
  japaneseNameMetadata,
} from '@/lib/auth/metadata';
import type { UserAddress } from '@/lib/auth/types';
import { normalizeTitleValue, resolveTitleForSubmit } from '@/lib/forms/titleOptions';
import type { AddressFormData, ExtendedAddressFormData } from '@/lib/validations/address';

// -------------------------------------------------------------------
// Metadata Keys
// -------------------------------------------------------------------

export const ADDRESS_METADATA = {
  houseMansionName: 'address.house_mansion',
  apartmentNumber: 'address.apartment_number',
  vatNumber: 'address.vatNumber',
  abn: 'address.abn',
} as const;

// -------------------------------------------------------------------
// Type Conversions
// -------------------------------------------------------------------

/**
 * Convert UserAddress (from auth) to form data
 */
function titleFromAddress(address: UserAddress | null): string {
  if (!address) return '';
  return (
    normalizeTitleValue(getMetaValue(address.metadata ?? [], ACCOUNT_METADATA.personalTitle)) ?? ''
  );
}

export function addressToFormData(address: UserAddress | null): ExtendedAddressFormData {
  if (!address) {
    return {
      title: '',
      firstName: '',
      lastName: '',
      firstNameKanji: '',
      lastNameKanji: '',
      firstNameKatakana: '',
      lastNameKatakana: '',
      companyName: '',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      postalCode: '',
      countryArea: '',
      country: '',
      phone: '',
      houseMansionName: '',
      apartmentNumber: '',
      vatNumber: '',
      abn: '',
      isDefaultShippingAddress: false,
      isDefaultBillingAddress: false,
    };
  }

  const metadata = address.metadata ?? [];
  const isJP = address.country?.code === 'JP';
  const jpNames = getJapaneseNameFromMetadata(metadata);
  const hasStoredKanji = Boolean(jpNames.firstNameKanji || jpNames.lastNameKanji);

  return {
    title: titleFromAddress(address),
    firstName: isJP && hasStoredKanji ? jpNames.firstNameRomaji : address.firstName || '',
    lastName: isJP && hasStoredKanji ? jpNames.lastNameRomaji : address.lastName || '',
    firstNameKanji: hasStoredKanji ? jpNames.firstNameKanji : '',
    lastNameKanji: hasStoredKanji ? jpNames.lastNameKanji : '',
    firstNameKatakana: jpNames.firstNameKatakana,
    lastNameKatakana: jpNames.lastNameKatakana,
    companyName: address.companyName || '',
    streetAddress1: address.streetAddress1 || '',
    streetAddress2: address.streetAddress2 || '',
    city: address.city || '',
    postalCode: address.postalCode || '',
    countryArea: address.countryArea || '',
    country: address.country?.code || '',
    phone: address.phone || '',
    houseMansionName: getMetaValue(metadata, ADDRESS_METADATA.houseMansionName) || '',
    apartmentNumber: getMetaValue(metadata, ADDRESS_METADATA.apartmentNumber) || '',
    vatNumber: getMetaValue(metadata, ADDRESS_METADATA.vatNumber) || '',
    abn: getMetaValue(metadata, ADDRESS_METADATA.abn) || '',
    isDefaultShippingAddress: address.isDefaultShippingAddress || false,
    isDefaultBillingAddress: address.isDefaultBillingAddress || false,
  };
}

/**
 * Convert form data to Saleor AddressInput.
 * JP: Saleor firstName/lastName are Kanji (checkout parity); Romaji/Katakana go to metadata.
 */
export function formDataToInput(
  data: AddressFormData &
    Partial<
      Pick<
        ExtendedAddressFormData,
        'firstNameKanji' | 'lastNameKanji' | 'firstNameKatakana' | 'lastNameKatakana'
      >
    >
): SaleorAddressInput {
  const isJP = data.country === 'JP';
  const kanjiFirst = isJP ? data.firstNameKanji?.trim() : '';
  const kanjiLast = isJP ? data.lastNameKanji?.trim() : '';

  const metadata: Array<{ key: string; value: string }> = [];
  const resolvedTitle = resolveTitleForSubmit(data.title, data.country);
  if (resolvedTitle) {
    metadata.push({ key: ACCOUNT_METADATA.personalTitle, value: resolvedTitle });
  }
  if (isJP) {
    metadata.push(
      ...japaneseNameMetadata({
        firstNameKanji: kanjiFirst,
        lastNameKanji: kanjiLast,
        firstNameKatakana: data.firstNameKatakana,
        lastNameKatakana: data.lastNameKatakana,
        firstNameRomaji: data.firstName,
        lastNameRomaji: data.lastName,
      })
    );
  }

  return {
    firstName: kanjiFirst || data.firstName,
    lastName: kanjiLast || data.lastName,
    companyName: data.companyName || '',
    streetAddress1: data.streetAddress1,
    streetAddress2: data.streetAddress2 || '',
    city: data.city,
    postalCode: data.postalCode,
    countryArea: data.countryArea || '',
    country: data.country as SaleorAddressInput['country'],
    phone: data.phone || '',
    ...(metadata.length > 0 && { metadata }),
  };
}

/**
 * Convert extended form data to Saleor AddressInput with metadata
 */
export function extendedFormDataToInput(data: ExtendedAddressFormData): SaleorAddressInput {
  const baseInput = formDataToInput(data);
  const metadata: Array<{ key: string; value: string }> = [...(baseInput.metadata ?? [])];

  // Add Japan-specific metadata
  if (data.country === 'JP') {
    if (data.houseMansionName) {
      metadata.push({ key: ADDRESS_METADATA.houseMansionName, value: data.houseMansionName });
    }
    if (data.apartmentNumber) {
      metadata.push({ key: ADDRESS_METADATA.apartmentNumber, value: data.apartmentNumber });
    }
  }

  // Add UK VAT number
  if (data.country === 'GB' && data.vatNumber) {
    metadata.push({ key: ADDRESS_METADATA.vatNumber, value: data.vatNumber });
  }

  // Add Australia ABN
  if (data.country === 'AU' && data.abn) {
    metadata.push({ key: ADDRESS_METADATA.abn, value: data.abn });
  }

  return {
    ...baseInput,
    ...(metadata.length > 0 && { metadata }),
  };
}

// -------------------------------------------------------------------
// Address Formatting
// -------------------------------------------------------------------

/**
 * Format address for display
 * Returns formatted lines for UI display
 */
export function formatAddressLines(address: UserAddress | null): string[] {
  if (!address) return [];

  const lines: string[] = [];

  // Name
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
  if (name) lines.push(name);

  // Company
  if (address.companyName) lines.push(address.companyName);

  // Street
  if (address.streetAddress1) lines.push(address.streetAddress1);
  if (address.streetAddress2) lines.push(address.streetAddress2);

  // City, State, Postal
  const cityLine = [address.city, address.countryArea, address.postalCode]
    .filter(Boolean)
    .join(', ');
  if (cityLine) lines.push(cityLine);

  // Country
  if (address.country?.country) lines.push(address.country.country);

  return lines;
}

/**
 * Format address as single line
 */
export function formatAddressSingleLine(address: UserAddress | null): string {
  return formatAddressLines(address).join(', ');
}

/**
 * Format address for Japan (different order)
 */
export function formatAddressLinesJapan(address: UserAddress | null): string[] {
  if (!address) return [];

  const lines: string[] = [];

  // Postal code first
  if (address.postalCode) lines.push(`〒${address.postalCode}`);

  // Prefecture + City
  const location = [address.countryArea, address.city].filter(Boolean).join(' ');
  if (location) lines.push(location);

  // Street address
  if (address.streetAddress1) lines.push(address.streetAddress1);
  if (address.streetAddress2) lines.push(address.streetAddress2);

  // Name last
  const name = [address.lastName, address.firstName].filter(Boolean).join(' ');
  if (name) lines.push(name);

  return lines;
}

// -------------------------------------------------------------------
// Address Comparison
// -------------------------------------------------------------------

/**
 * Compare two addresses for equality (ignoring ID and metadata)
 */
export function areAddressesEqual(a: UserAddress | null, b: UserAddress | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;

  return (
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.companyName === b.companyName &&
    a.streetAddress1 === b.streetAddress1 &&
    a.streetAddress2 === b.streetAddress2 &&
    a.city === b.city &&
    a.postalCode === b.postalCode &&
    a.countryArea === b.countryArea &&
    a.country?.code === b.country?.code &&
    a.phone === b.phone
  );
}

// -------------------------------------------------------------------
// Country Helpers
// -------------------------------------------------------------------

/**
 * Get country area label by country code
 */
export function getCountryAreaLabel(countryCode: string): string {
  switch (countryCode) {
    case 'US':
      return 'State';
    case 'CA':
      return 'Province';
    case 'AU':
      return 'State/Territory';
    case 'JP':
      return 'Prefecture';
    case 'GB':
      return 'County';
    default:
      return 'State/Region';
  }
}

/**
 * Check if country requires state/province
 */
export function requiresCountryArea(countryCode: string): boolean {
  return ['US', 'CA', 'AU', 'JP'].includes(countryCode);
}

/**
 * Check if country uses postal codes
 */
export function hasPostalCode(countryCode: string): boolean {
  // Most countries use postal codes, these are exceptions
  const noPostalCode = ['AE', 'AG', 'AO', 'BS', 'BZ', 'QA'];
  return !noPostalCode.includes(countryCode);
}

// -------------------------------------------------------------------
// Country Code Normalization
// -------------------------------------------------------------------

/**
 * Normalize a raw country value to an uppercase ISO 3166-1 alpha-2 code
 * (the shape Saleor's CountryCode enum expects, e.g. "GB", "US").
 *
 * Returns undefined for anything that is not a two-letter code (full names
 * like "United Kingdom", alpha-3 codes like "GBR", empty values) so callers
 * can fall back to a trusted country instead of using an invalid value.
 */
export function normalizeCountryCode(raw: string | null | undefined): string | undefined {
  const trimmed = raw?.trim();
  if (!trimmed || !/^[A-Za-z]{2}$/.test(trimmed)) {
    return undefined;
  }
  return trimmed.toUpperCase();
}

// -------------------------------------------------------------------
// UK Postcode Formatting
// -------------------------------------------------------------------

/**
 * Regex to match UK postcodes (various formats)
 * Examples: SW1A1AA, SW1A 1AA, EC1A1BB, W1A0AX
 */
export const UK_POSTCODE_REGEX = /^([A-Z]{1,2}[0-9][0-9A-Z]?)\s*([0-9][A-Z]{2})$/i;

/**
 * Format UK postcode with proper spacing
 * E.g., "SW1A1AA" → "SW1A 1AA"
 */
export function formatUKPostcode(postcode: string): string {
  if (!postcode) return postcode;

  const trimmed = postcode.trim().toUpperCase().replace(/\s+/g, '');
  const match = trimmed.match(UK_POSTCODE_REGEX);

  if (match) {
    return `${match[1]} ${match[2]}`;
  }

  return postcode;
}

/**
 * Validate UK postcode format
 */
export function isValidUKPostcode(postcode: string): boolean {
  if (!postcode) return false;
  const cleaned = postcode.trim().replace(/\s+/g, '');
  return UK_POSTCODE_REGEX.test(cleaned);
}
